import type { JSONValue, JsonMap } from '@st/util/json-value'
import { ReactNode, createContext, useCallback, useContext } from 'react'

export type ReadHook = (key: string) => JSONValue
export type WriteHook = () => (values: JsonMap) => void

export type EvalExprHook = (expr: string | undefined) => JSONValue

export type FormBinding = {
  useRead: ReadHook
  useWrite: WriteHook
  useEvalExpr: EvalExprHook
}

const NO_READ: ReadHook = () => null
const NO_WRITE: WriteHook = () => () => null
const NO_EVAL_EXPR: EvalExprHook = () => false

const ReadWriteContext = createContext<FormBinding | undefined>(undefined)

export function useReadWriteContext(): FormBinding {
  return (
    useContext(ReadWriteContext) ?? {
      useRead: NO_READ,
      useWrite: NO_WRITE,
      useEvalExpr: NO_EVAL_EXPR
    }
  )
}

export function useInputValue<T>(
  inputKey: string,
  cast: (value: unknown) => T
): [T, (value: string | undefined) => void] {
  const { useRead, useWrite } = useReadWriteContext()

  const value = cast(useRead(inputKey))
  const setValues = useWrite()

  const onChange = useCallback(
    (value: string | undefined) => {
      setValues({ [inputKey]: value ?? null })
    },
    [inputKey]
  )

  return [value, onChange]
}

type ReadWriteProviderProps = {
  children: ReactNode
  useRead?: ReadHook
  useWrite?: WriteHook
  useEvalExpr?: EvalExprHook
}

export function ReadWriteProvider({
  children,
  useRead = NO_READ,
  useWrite = NO_WRITE,
  useEvalExpr = NO_EVAL_EXPR
}: ReadWriteProviderProps) {
  return (
    <ReadWriteContext.Provider
      value={{
        useRead,
        useWrite,
        useEvalExpr
      }}
    >
      {children}
    </ReadWriteContext.Provider>
  )
}
